import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Router from '@/components/common/router';
import { API_ROUTES, NEXTJS_API_ROUTES } from '@/constants/apiPaths';
import { fetchGarageTimeline, clearTimeline } from './garageTimeline';
import {
    AUTH_ENTITY_TYPE,
    AUTH_KEYS,
    GARAGE_FRONTEND_SOURCE_ID,
} from '@/constants/common';
interface Damage {
    damage_id: number;
    status: 'REJECTED' | 'APPROVED';
}

interface FinalEstimate {
    discount_amount: number;
    total_amount: number;
    net_amount: number;
}

interface Data {
    order_id: number;
    assessment_id: number;
    damages: Damage[];
    final_estimate: FinalEstimate;
}

interface ApprovePayload {
    data: Data;
    cb?: () => void;
}

interface SavePayload {
    order_id: number;
    assessment_id: number;
    damages: Damage[];
}

interface EstimateState {
    loading: 'idle' | 'pending' | 'fulfilled' | 'rejected';
    error: string | null;
}

export const approveEstimate = createAsyncThunk(
    'estimate/approve',
    async (reqObj: ApprovePayload, { dispatch }) => {
        const payload = reqObj.data;
        try {
            const options = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
                    [AUTH_KEYS.X_ENTITY]: [AUTH_ENTITY_TYPE.ORDER],
                    [AUTH_KEYS.X_ENTITY_ID]: payload.order_id,
                },
                credentials: 'include',
                body: JSON.stringify(payload),
            };
            await fetch(NEXTJS_API_ROUTES.APPROVE_ESTIMATE, options as any);
            await dispatch(clearTimeline({}));
            if (reqObj.cb) {
                reqObj.cb();
            }
            return payload.order_id;
        } catch (error) {
            console.error('Error approving estimate:', error);
            throw error;
        }
    }
);

export const saveEstimate = createAsyncThunk(
    'assessment/save',
    async (payload: SavePayload, { dispatch }) => {
        try {
            const options = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
                    [AUTH_KEYS.X_ENTITY]: [AUTH_ENTITY_TYPE.ORDER],
                    [AUTH_KEYS.X_ENTITY_ID]: payload.order_id,
                },
                credentials: 'include',
                body: JSON.stringify(payload),
            };

            await fetch(NEXTJS_API_ROUTES.SAVE_ESTIMATE, options as any);
            await dispatch(clearTimeline({}));
            return payload.order_id;
        } catch (error) {
            console.error('Error saving estimate:', error);
            throw error;
        }
    }
);

const estimateSlice = createSlice({
    name: 'estimate',
    initialState: {
        loading: 'idle',
        error: null,
    } as EstimateState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(approveEstimate.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(approveEstimate.fulfilled, (state: any, action: any) => {
                state.loading = 'fulfilled';
                // Router.push(`/approved?orderId=${action.payload}`);
            })
            .addCase(approveEstimate.rejected, (state, action) => {
                state.loading = 'rejected';
                state.error = action.error.message || 'An error occurred.';
            })

            .addCase(saveEstimate.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
            })
            .addCase(saveEstimate.fulfilled, (state: any, action: any) => {
                state.loading = 'fulfilled';
                Router.push(`/estimate-approval?orderId=${action.payload}`);
            })
            .addCase(saveEstimate.rejected, (state, action) => {
                state.loading = 'rejected';
                state.error = action.error.message || 'An error occurred.';
            });
    },
});

export default estimateSlice.reducer;
